import { Formik, FormikConfig } from 'formik'
import { ReactElement } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { GeneralInformationFormCard } from 'features/Places/components/GeneralInformationFormCard'
import { CoordinatesFormCard } from 'features/Places/components/CoordinatesFormCard'
import { AddressFormCard } from 'features/Places/components/AddressFormCard'
import { INewPlaceForm } from 'features/Places/types'
import { mapAmenitiesToForm } from 'features/Places/utils'
import { getUnprocessableErrors } from 'utils/error'
import { newPlaceSchema } from './schema'
import { Column } from 'components/v2/Column'
import { Row } from 'components/v2/Row'
import { PlaceMap } from 'features/Places/components/PlaceMap'
import { GTPlaceAmenity, GTPlaceV2TypeInput, GTTPlace, GTTPlaceVertical } from 'gql/types.generated'
import { ApolloError } from '@apollo/client'

export const NewPlace = ({
  place,
  isEdit,
  onSubmit,
  error,
  s3Error,
  loading
}: {
  place?: GTTPlace
  isEdit?: boolean
  onSubmit: FormikConfig<INewPlaceForm>['onSubmit']
  error?: ApolloError
  s3Error?: string
  loading?: boolean
}): ReactElement => {
  const intl = useIntl()

  let city = {
    name: ''
  }

  if (place?.address?.cityName) {
    city = {
      name: place?.address.cityName
    }
  }

  const address = {
    line1: place?.address?.line1 || '',
    line2: place?.address?.line2 || '',
    zipCode: place?.address?.zipCode || '',
    countryCode: place?.address?.countryCode || ''
  }

  let vertical: GTTPlaceVertical | '' = ''
  if (isEdit) {
    if (place?.company?.vertical) {
      vertical = place?.company.vertical
    } else {
      vertical = GTTPlaceVertical.Travel
    }
  }

  const initialPlace: INewPlaceForm = {
    name: place?.name || '',
    type: (place?.type as unknown as GTPlaceV2TypeInput) || '',
    tags: place?.tags || [],
    param: place?.param || '',
    code: place?.code || '',
    area: place?.area || '',
    directions: place?.directions || '',
    coordinates: place?.coordinates ?? {
      latitude: '',
      longitude: ''
    },
    geofenceRadius: place?.geofenceRadius || null,
    city: {
      id: place?.parent?.id || '',
      countryCode: place?.address?.countryCode || '',
      stateCode: place?.address?.stateCode || '',
      ...city
    },
    address,
    amenities: mapAmenitiesToForm((place?.amenities as unknown as GTPlaceAmenity[]) ?? [], intl),
    company: place?.company || {
      id: '',
      name: '',
      vertical: ''
    },
    businessClient: place?.businessClient || {
      id: '',
      name: ''
    },
    businessSite: place?.businessSite || {
      id: '',
      name: ''
    },
    vertical,
    photo: {
      file: null,
      path: place?.photo?.path || '',
      name: place?.photo?.name || ''
    }
  }

  return (
    <Formik initialValues={initialPlace} validationSchema={newPlaceSchema(intl)} onSubmit={onSubmit}>
      {({ handleSubmit, dirty, isSubmitting }): ReactElement => {
        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit} isLargeContent>
            <Heading.H3 capitalize>
              {isEdit ? (
                <FormattedMessage id="places.edit.title" defaultMessage="Edit place" />
              ) : (
                <FormattedMessage id="places.new.title" defaultMessage="Add place" />
              )}
            </Heading.H3>
            <Row noMargin>
              <Column width="70%">
                <Row>
                  <Column>
                    <GeneralInformationFormCard isEdit={isEdit} />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <CoordinatesFormCard place={place as GTTPlace} />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <AddressFormCard isEdit={isEdit} />
                  </Column>
                </Row>
              </Column>
              <Column>
                <PlaceMap />
              </Column>
            </Row>
            {s3Error && (
              <div>
                <ErrorMessage>{s3Error}</ErrorMessage>
              </div>
            )}
            {error && (
              <div>
                <ErrorMessage errors={getUnprocessableErrors(error)}>
                  <FormattedMessage
                    id="places.new.form.error"
                    defaultMessage="There was an error while saving your place."
                  />
                </ErrorMessage>
              </div>
            )}
            <FormLayout.Actions>
              <Button as={NavLink} kind="cancel" to="/places" disabled={loading || isSubmitting}>
                <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="submit" kind="primary" disabled={!dirty} loading={loading || isSubmitting}>
                <FormattedMessage id="dictionary.save" defaultMessage="Save" />
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
